import { KGDataFrameProto } from "build-ts/dataframe";
import { FilterProto } from "build-ts/metadata";
import { BenchmarkType, ColumnType, TimeBucket } from "build-ts/enums";
import React from "react";
import {
	Box,
	HStack,
	VStack,
	Text,
	Icon,
} from "@chakra-ui/react";
import { MdBarChart, MdCallSplit, MdFilterAlt } from "react-icons/md";
import { TbMathFunction } from "react-icons/tb";
import { LuGitCompare } from "react-icons/lu";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getDateFilterName } from "utils/nameUtils";

interface DataFramePlanProps {
	dataFrame: KGDataFrameProto;
	type: "query" | "calculations";
}

/* -------------------- Helpers -------------------- */

const getTimeBucketLabel = (bucket: TimeBucket): string => {
	switch (bucket) {
		case TimeBucket.DAILY: return "daily";
		case TimeBucket.WEEKLY: return "weekly";
		case TimeBucket.MONTHLY: return "monthly";
		case TimeBucket.QUARTERLY: return "quarterly";
		case TimeBucket.YEARLY: return "yearly";
		default: return "";
	}
};

const Sep: React.FC = () => (
	<Text as="span" color="gray.400" mx={1.5} fontWeight="400" userSelect="none">|</Text>
);

/** One plan row: icon + muted label + flowing content */
const PlanRow: React.FC<{
	icon: React.ElementType;
	iconSize?: number;
	label: string;
	children: React.ReactNode;
}> = ({ icon, iconSize = 3, label, children }) => (
	<HStack align="flex-start" spacing={2} py={1.5} borderBottom="1px solid" borderColor="gray.100">
		<HStack spacing={1.5} flexShrink={0} pt="1px" minW="130px">
			<Icon as={icon} boxSize={iconSize} color="purple.500" />
			<Text fontSize="xs" fontWeight="600" color="gray.500" m={0} whiteSpace="nowrap">
				{label}
			</Text>
		</HStack>
		<Box flex={1} fontSize="xs" color="gray.800">
			{children}
		</Box>
	</HStack>
);

/* -------------------- Component -------------------- */

const DataFramePlan: React.FC<DataFramePlanProps> = ({ dataFrame, type }) => {
	const { kg_observation, ordered_columns } = dataFrame;

	const getFilterValue = (filter: FilterProto): string => {
		if (filter?.values?.length > 0) {
			return filter.values.map((v: any) => v.toString().trim()).join(", ");
		}
		if (filter?.date_filter?.absolute_date_filter) {
			return getDateFilterName(filter.date_filter.absolute_date_filter);
		}
		if (filter?.date_filter) {
			return getDateFilterName(filter.date_filter);
		}
		return "";
	};

	const renderQuery = () => {
		const rawFilters: FilterProto[] = kg_observation?.filters || [];
		const metrics = kg_observation?.metrics || [];
		const obsColumns = kg_observation?.columns || [];
		const benchmarks = kg_observation?.benchmarks || [];
		const comparisonCols = ordered_columns.filter(
			(col) => col.column_hash?.actuals_id && col.column_hash?.benchmark_id
		);

		const hasFilters = rawFilters.length > 0;
		const hasMetrics = metrics.length > 0 || obsColumns.some((c) => c.column_type === ColumnType.MEASURE);
		const hasGroupBy = obsColumns.some((col) => col.column_type !== ColumnType.MEASURE);
		const hasComparisons = comparisonCols.length > 0;

		if (!hasFilters && !hasMetrics && !hasGroupBy && !hasComparisons) {
			return null;
		}

		return (
			<VStack align="stretch" spacing={0}>
				{/* Filters */}
				{hasFilters && (
					<PlanRow icon={MdFilterAlt} label="Filters" iconSize={4}>
						<HStack spacing={0} flexWrap="wrap" rowGap={1} align="center">
							{rawFilters.map((filter, i) => (
								<React.Fragment key={i}>
									<Text as="span" fontSize="xs" m={0} fontFamily="mono">
										<Text as="span" fontWeight="700" color="gray.800">{filter?.kg_column_header?.name}</Text>
										<Text as="span" color="gray.500" mx={1}>{getOperatorSymbol(filter?.operator!)}</Text>
										<Text as="span" color="gray.700">{getFilterValue(filter)}</Text>
									</Text>
									{i < rawFilters.length - 1 && <Sep />}
								</React.Fragment>
							))}
						</HStack>
					</PlanRow>
				)}

				{/* Metrics */}
				{hasMetrics && (
					<PlanRow icon={MdBarChart} iconSize={4} label="Metrics">
						<HStack spacing={0} flexWrap="wrap" rowGap={1} align="center">
							{[
								...metrics.map((metric, i) => {
									const col = ordered_columns.find(
										(c) => c.column_hash?.actuals_id === metric.header?.id && !c.column_hash?.benchmark_id
									);
									const hasMeasuresAfter = obsColumns.some((c) => c.column_type === ColumnType.MEASURE);
									return (
										<React.Fragment key={`m-${i}`}>
											<Text as="span" fontSize="xs" m={0}>
												<Text as="span" fontWeight="600" color="gray.800">{metric.header?.name}</Text>
												{col && (
													<Text as="span" color="gray.500" fontFamily="mono"> (column: {col.output_column_name})</Text>
												)}
											</Text>
											{(i < metrics.length - 1 || hasMeasuresAfter) && <Sep />}
										</React.Fragment>
									);
								}),
								...obsColumns
									.filter((c) => c.column_type === ColumnType.MEASURE)
									.map((obsCol, i, arr) => {
										const col = ordered_columns.find(
											(c) => c.column_hash?.actuals_id === obsCol.column_id && !c.column_hash?.benchmark_id
										);
										return (
											<React.Fragment key={`ms-${i}`}>
												<Text as="span" fontSize="xs" m={0}>
													<Text as="span" fontWeight="600" color="gray.800">{obsCol.kg_column_header?.name}</Text>
													{col && (
														<Text as="span" color="gray.500" fontFamily="mono"> (column: {col.output_column_name})</Text>
													)}
												</Text>
												{i < arr.length - 1 && <Sep />}
											</React.Fragment>
										);
									}),
							]}
						</HStack>
					</PlanRow>
				)}

				{/* Group By */}
				{hasGroupBy && (
					<PlanRow icon={MdCallSplit} iconSize={4} label="Group by">
						<HStack spacing={0} flexWrap="wrap" rowGap={1} align="center">
							{obsColumns
								.filter((obsCol) => obsCol.column_type !== ColumnType.MEASURE)
								.map((obsCol, i, arr) => {
									const col = ordered_columns.find((c) => c.column_hash?.actuals_id === obsCol.column_id);
									const bucket = obsCol.time_bucket ? getTimeBucketLabel(obsCol.time_bucket) : "";
									return (
										<React.Fragment key={i}>
											<Text as="span" fontSize="xs" m={0}>
												<Text as="span" fontWeight="600" color="gray.800">{obsCol.kg_column_header?.name}</Text>
												{col && (
													<Text as="span" color="gray.500" fontFamily="mono">
														{" "}(column: {col.output_column_name}{bucket ? `, ${bucket}` : ""})
													</Text>
												)}
											</Text>
											{i < arr.length - 1 && <Sep />}
										</React.Fragment>
									);
								})}
						</HStack>
					</PlanRow>
				)}

				{/* Comparisons */}
				{hasComparisons && (
					<PlanRow icon={LuGitCompare} iconSize={4} label="Comparisons">
						<VStack align="stretch" spacing={0.5} alignItems="flex-start">
							{comparisonCols.map((col, i) => {
								const actualsCol = ordered_columns.find(
									(c) => c.column_hash?.actuals_id === col.column_hash?.actuals_id && !c.column_hash?.benchmark_id
								);
								const benchmark = benchmarks.find((b) => b.header?.id === col.column_hash?.benchmark_id);
								let suffix = "";
								if (benchmark?.benchmark_type === BenchmarkType.VERSUS_BENCHMARK) {
									const versusFilters: string[] = [];
									benchmark.versus_filters?.forEach((f) => {
										if (f.date_filter) {
											versusFilters.push(getDateFilterName(f.date_filter));
										} else {
											let name = f.kg_column_header?.name;
											let operator = "";
											if (f.operator) {
												operator = getOperatorSymbol(f.operator);
											}
											let values = f.values.join(", ");
											versusFilters.push(`${name} ${operator} ${values}`);
										}
									});
									if (versusFilters.length > 0) suffix = `modified filters: ${versusFilters.join("; ")}`;
								} else if (benchmark?.benchmark_type === BenchmarkType.TARGET_BENCHMARK && benchmark.header?.name) {
									suffix = benchmark.header.name;
								}
								return (
									<Text key={i} fontSize="xs" m={0}>
										<Text as="span" fontWeight="600" color="gray.800">
											{actualsCol?.output_column_name ?? col.column_hash?.actuals_id}
										</Text>
										<Text as="span" color="gray.500" mx={1}>vs</Text>
										<Text as="span" fontWeight="600" color="gray.800">{col.output_column_name}</Text>
										{suffix && <Text as="span" color="gray.400"> — {suffix}</Text>}
									</Text>
								);
							})}
						</VStack>
					</PlanRow>
				)}
			</VStack>
		);
	};

	const renderCalculation = () => {
		const calcCols = ordered_columns.filter((col) => col.eval_expression);
		if (calcCols.length === 0) return null;

		return (
			<VStack align="stretch" spacing={0}>
				<PlanRow icon={TbMathFunction} iconSize={4} label="Calculations">
					<VStack align="flex-start" spacing={1}>
						{calcCols.map((col, i) => (
							<Text key={i} fontSize="xs" fontFamily="mono" m={0} textAlign="left">
								<Text as="span" fontWeight="700" color="gray.800">{col.output_column_name}</Text>
								<Text as="span" color="gray.400" mx={1}>=</Text>
								<Text as="span" color="gray.600">{col.eval_expression}</Text>
							</Text>
						))}
					</VStack>
				</PlanRow>
			</VStack>
		);
	};

	return (
		<Box px={2} py={1}>
			{type === "query" ? renderQuery() : renderCalculation()}
		</Box>
	);
};

export default DataFramePlan;
